import { useState, useMemo, useCallback } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useBreadcrumbs } from '../../../../hooks/useBreadcrumbs'
import { tokensApi } from '../../../../lib/api/tokens'
import { Button } from '../../../../components/ui/button'
import { Card, CardContent } from '../../../../components/ui/card'
import { Badge } from '../../../../components/ui/badge'
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../../../../components/ui/alert-dialog'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../../../../components/ui/dialog'
import { AppIcon } from '../../../../components/ui/icon'
import { useTranslation } from 'react-i18next'
import { PageHeader } from '../../../../components/shared/PageHeader'
import { SECTION_ICONS } from '../../../../lib/section-icons'
import { EmptyState } from '../../../../components/ui/empty-state'
import { mdiCalendar, mdiCheck, mdiClockOutline, mdiContentCopy, mdiEmailOutline, mdiPlus, mdiTrashCanOutline } from '@mdi/js'

export const Route = createFileRoute('/$org/user/me/email-ingestion')({
    component: UserEmailIngestionPage,
})

interface UserToken {
    uid: string
    type: string
    properties?: {
        name?: string
        email?: string
        created_with?: {
            user_agent?: string
            remote_addr?: string
        }
    }
    created_at: string
    last_used_at?: string | null
    expires_at: string
}

function UserEmailIngestionPage() {
    const { org } = Route.useParams()
    const queryClient = useQueryClient()
    const { t } = useTranslation('users')
    const userUid = 'me'

    const [isCreateOpen, setIsCreateOpen] = useState(false)
    const [tokenName, setTokenName] = useState('')
    const [expiresInDays, setExpiresInDays] = useState(365)
    const [createdToken, setCreatedToken] = useState<string | null>(null)
    const [copied, setCopied] = useState(false)
    const [deleteTokenUid, setDeleteTokenUid] = useState<string | null>(null)

    const breadcrumbs = useMemo(() => [
        { title: t('breadcrumb.home'), path: `/${org}` },
        { title: t('breadcrumb.user'), path: `/${org}/user/me/profile` },
        { title: t('breadcrumb.email_ingestion') },
    ], [org, t])
    useBreadcrumbs(breadcrumbs)

    const { data: emailData, isLoading } = useQuery({
        queryKey: ['userTokens', org, userUid, 'email'],
        queryFn: () => tokensApi.getUserTokens(org, userUid, 'email'),
        enabled: !!userUid,
    })

    const createMutation = useMutation({
        mutationFn: () => tokensApi.createToken(org, userUid, tokenName, 'email', expiresInDays),
        onSuccess: (data) => {
            setCreatedToken(data.token)
            setTokenName('')
            queryClient.invalidateQueries({ queryKey: ['userTokens', org, userUid, 'email'] })
        },
    })

    const deleteMutation = useMutation({
        mutationFn: (tokenUid: string) => tokensApi.deletePAT(org, userUid, tokenUid),
        onSuccess: () => {
            setDeleteTokenUid(null)
            queryClient.invalidateQueries({ queryKey: ['userTokens', org, userUid, 'email'] })
        },
    })

    const handleCopy = useCallback((text: string) => {
        navigator.clipboard.writeText(text)
        setCopied(true)
        setTimeout(() => setCopied(false), 2000)
    }, [])

    const handleCloseCreate = useCallback(() => {
        setIsCreateOpen(false)
        setCreatedToken(null)
        setTokenName('')
        setCopied(false)
    }, [])

    const formatDate = (dateString: string | null) => {
        if (!dateString) return t('email_ingestion.never')
        return new Intl.DateTimeFormat('en-US', {
            dateStyle: 'medium',
            timeStyle: 'short',
        }).format(new Date(dateString))
    }

    const isExpired = (expiresAt: string) => new Date(expiresAt) < new Date()

    const tokens = (emailData?.tokens || []) as UserToken[]

    const displayValue = createdToken ? `${createdToken}@buildtory.io` : null

    return (
        <div className="space-y-6" data-testid="user-email-ingestion-page">
            <div>
                <PageHeader
                    icon={SECTION_ICONS.emailIngestion}
                    title={t('email_ingestion.title')}
                    description={t('email_ingestion.subtitle')}
                    actions={
                    <>
                    <Dialog open={isCreateOpen} onOpenChange={(open) => {
                        if (!open) handleCloseCreate()
                        else setIsCreateOpen(true)
                    }}>
                        <DialogTrigger asChild>
                            <Button data-testid="create-token-btn">
                                <AppIcon path={mdiPlus} size="sm" className="mr-2" />
                                {t('email_ingestion.new_email_address')}
                            </Button>
                        </DialogTrigger>
                        <DialogContent>
                            <DialogHeader>
                                <DialogTitle>
                                    {createdToken ? t('email_ingestion.email_created_title') : t('email_ingestion.create_email_title')}
                                </DialogTitle>
                                <DialogDescription>
                                    {createdToken
                                        ? t('email_ingestion.email_created_description')
                                        : t('email_ingestion.create_email_description')}
                                </DialogDescription>
                            </DialogHeader>

                            {createdToken ? (
                                <div className="space-y-4">
                                    <div className="flex items-center gap-2">
                                        <code
                                            className="flex-1 px-3 py-2 bg-muted rounded-md text-sm font-mono break-all"
                                            data-testid="token-email-address"
                                        >
                                            {displayValue}
                                        </code>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => handleCopy(displayValue || '')}
                                            data-testid="token-copy-email-btn"
                                        >
                                            {copied ? <AppIcon path={mdiCheck} size="sm" /> : <AppIcon path={mdiContentCopy} size="sm" />}
                                        </Button>
                                    </div>
                                    <DialogFooter>
                                        <Button onClick={handleCloseCreate}>{t('email_ingestion.done')}</Button>
                                    </DialogFooter>
                                </div>
                            ) : (
                                <form onSubmit={(e) => { e.preventDefault(); createMutation.mutate() }} className="space-y-4">
                                    <div>
                                        <label htmlFor="tokenName" className="block text-sm font-medium text-foreground mb-1">
                                            {t('email_ingestion.address_name_label')}
                                        </label>
                                        <input
                                            id="tokenName"
                                            type="text"
                                            value={tokenName}
                                            onChange={(e) => setTokenName(e.target.value)}
                                            placeholder={t('email_ingestion.address_name_placeholder')}
                                            required
                                            className="block w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                                            data-testid="token-name-input"
                                        />
                                    </div>
                                    <div>
                                        <label htmlFor="expiresIn" className="block text-sm font-medium text-foreground mb-1">
                                            {t('email_ingestion.expiration_label')}
                                        </label>
                                        <select
                                            id="expiresIn"
                                            value={expiresInDays}
                                            onChange={(e) => setExpiresInDays(Number(e.target.value))}
                                            className="block w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                                            data-testid="email-expires-select"
                                        >
                                            <option value={30}>{t('email_ingestion.days_30')}</option>
                                            <option value={90}>{t('email_ingestion.days_90')}</option>
                                            <option value={180}>{t('email_ingestion.days_180')}</option>
                                            <option value={365}>{t('email_ingestion.year_1')}</option>
                                            <option value={1095}>{t('email_ingestion.years_3')}</option>
                                            <option value={1825}>{t('email_ingestion.years_5')}</option>
                                        </select>
                                    </div>
                                    <DialogFooter>
                                        <Button type="button" variant="outline" onClick={handleCloseCreate}>{t('common:actions.cancel')}</Button>
                                        <Button
                                            type="submit"
                                            disabled={createMutation.isPending || !tokenName.trim()}
                                            data-testid="create-email-submit"
                                        >
                                            {createMutation.isPending ? t('email_ingestion.creating') : t('email_ingestion.create_button')}
                                        </Button>
                                    </DialogFooter>
                                </form>
                            )}
                        </DialogContent>
                    </Dialog>
                    </>
                    }
                />
            </div>

            <div>
                {isLoading && (
                    <div className="flex items-center justify-center py-8">
                        <div className="motion-safe:animate-spin rounded-full h-6 w-6 border-b-2 border-primary mr-3"></div>
                        <span className="text-muted-foreground">{t('email_ingestion.loading')}</span>
                    </div>
                )}

                {!isLoading && tokens.length === 0 && (
                    <EmptyState
                        variant="inline"
                        icon={mdiEmailOutline}
                        title={t('email_ingestion.no_addresses_title')}
                        description={t('email_ingestion.no_addresses_description')}
                    />
                )}

                {tokens.length > 0 && (
                    <div className="grid gap-3">
                        {tokens.map((token) => {
                            const expired = isExpired(token.expires_at)
                            const emailAddress = token.properties?.email || ''
                            return (
                                <Card key={token.uid} className="hover:shadow-md transition-shadow">
                                    <CardContent className="p-4">
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-3 min-w-0">
                                                <AppIcon path={mdiEmailOutline} size="md" className="text-primary flex-shrink-0" />
                                                <div className="min-w-0">
                                                    <div className="flex items-center gap-2 mb-1">
                                                        <h3 className="text-sm font-medium text-foreground truncate">
                                                            {token.properties?.name || t('email_ingestion.unnamed')}
                                                        </h3>
                                                        {expired && <Badge variant="destructive">{t('email_ingestion.expired')}</Badge>}
                                                    </div>
                                                    {emailAddress && (
                                                        <div className="flex items-center gap-2 mb-1">
                                                            <code className="text-sm font-mono text-muted-foreground" data-testid="token-email-address">
                                                                {emailAddress}
                                                            </code>
                                                            <Button
                                                                variant="ghost"
                                                                size="sm"
                                                                className="h-6 w-6 p-0"
                                                                onClick={() => handleCopy(emailAddress)}
                                                                data-testid="token-copy-email-btn"
                                                            >
                                                                <AppIcon path={mdiContentCopy} size="xs" />
                                                            </Button>
                                                        </div>
                                                    )}
                                                    <div className="flex items-center gap-4 text-xs text-muted-foreground">
                                                        <span className="flex items-center gap-1">
                                                            <AppIcon path={mdiCalendar} size="xs" />
                                                            {t('email_ingestion.created', { date: formatDate(token.created_at) })}
                                                        </span>
                                                        <span className="flex items-center gap-1">
                                                            <AppIcon path={mdiClockOutline} size="xs" />
                                                            {t('email_ingestion.last_used', { date: formatDate(token.last_used_at || null) })}
                                                        </span>
                                                        <span>
                                                            {t('email_ingestion.expires', { date: formatDate(token.expires_at) })}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => setDeleteTokenUid(token.uid)}
                                                className="text-destructive hover:text-destructive"
                                                data-testid={`delete-token-btn-${token.uid}`}
                                            >
                                                <AppIcon path={mdiTrashCanOutline} size="sm" />
                                            </Button>
                                        </div>
                                    </CardContent>
                                </Card>
                            )
                        })}
                    </div>
                )}
            </div>

            <AlertDialog open={!!deleteTokenUid} onOpenChange={(open) => { if (!open) setDeleteTokenUid(null) }}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>{t('email_ingestion.delete_address_title')}</AlertDialogTitle>
                        <AlertDialogDescription>
                            {t('email_ingestion.delete_address_description')}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={deleteMutation.isPending}>{t('common:actions.cancel')}</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={() => deleteTokenUid && deleteMutation.mutate(deleteTokenUid)}
                            disabled={deleteMutation.isPending}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                            {deleteMutation.isPending ? t('email_ingestion.deleting') : t('common:actions.delete')}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    )
}
